import React, { useState } from 'react';

const IconNew = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ verticalAlign: '-2px' }}>
    <path d="M12 3l1.7 4.6L18 9.3l-4.3 1.7L12 15.5l-1.7-4.5L6 9.3l4.3-1.7z" />
  </svg>
);
const IconRepropose = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: '-2px' }}>
    <path d="M21 12a9 9 0 1 1-2.64-6.36M21 4v5h-5" />
  </svg>
);
const IconPriorita = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: '-3px' }}>
    <circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="3" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
  </svg>
);
const IconLock = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: '-2px', marginLeft: 6 }}>
    <rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

// Colore barra "lavorato/controllato": giallo se bassa, arancione media/alta, verde solo al 100%.
const barColor = (p) => (p >= 100 ? '#16a34a' : p >= 50 ? '#f97316' : '#eab308');

const COLS = [
  { key: 'tot', label: 'Tot' },
  { key: 'controllati', label: 'Contr.', bar: true },
  { key: 'tim', label: 'TIM' },
  { key: 'non_tim', label: 'NON TIM', strong: true },
  { key: 'pronti_nuovi', label: 'Nuovi', icon: <IconNew />, tip: 'Pronti all’export — nuovi', highlight: true },
  { key: 'pronti_riproposti', label: 'Scaduti', icon: <IconRepropose />, tip: 'Pronti all’export — scaduti (da riproporre)', highlight: true },
  { key: 'esportati', label: 'Esp.' },
];

/* Analisi per area (regione o provincia): tabella ordinabile + barra % lavorato.
   - onRowClick(nome): righe cliccabili (collegamento regione->province)
   - onToggleCheck(nome) + checkedSet: checkbox di selezione per l'export */
export default function AreaAnalysis({ title, areaLabel, rows, action, onRowClick, selected, checkedSet, onToggleCheck, onPriority, priorityNames, lockedNames }) {
  const [sortKey, setSortKey] = useState('non_tim');
  const sorted = [...rows].sort((a, b) => (b[sortKey] || 0) - (a[sortKey] || 0));
  const selectable = typeof onToggleCheck === 'function';
  const allChecked = selectable && sorted.length > 0 && sorted.every(r => checkedSet?.has(r.nome));

  return (
    <div className="evo-card aa-card">
      <div className="evo-card-head">
        <span>{title}</span>
        {action || null}
      </div>
      <div className="aa-scroll">
        <table className="aa-table">
          <thead>
            <tr>
              {selectable && (
                <th className="aa-chk">
                  <input type="checkbox" checked={allChecked}
                    onChange={() => onToggleCheck('__all__', sorted.map(r => r.nome), !allChecked)} />
                </th>
              )}
              <th className="aa-name">{areaLabel}</th>
              {COLS.map(c => (
                <th key={c.key}
                  className={`aa-num${sortKey === c.key ? ' aa-sort' : ''}${c.highlight ? ' aa-ready' : ''}`}
                  title={c.tip ? `${c.tip} · clicca per ordinare` : 'Ordina'}
                  onClick={() => setSortKey(c.key)}>
                  {c.icon ? <>{c.icon} {c.label}</> : c.label}{sortKey === c.key ? ' ↓' : ''}
                </th>
              ))}
              <th className="aa-bar-h">Lavorato</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(r => {
              const pct = r.tot ? Math.round((r.controllati / r.tot) * 100) : 0;
              const isSel = selected === r.nome;
              const inPriority = !!priorityNames?.has(r.nome);
              const isLocked = !!lockedNames?.has(r.nome);
              return (
                <tr key={r.nome}
                  className={`${onRowClick ? 'aa-click' : ''}${isSel ? ' aa-selected' : ''}`}
                  onClick={onRowClick ? () => onRowClick(r.nome) : undefined}>
                  {selectable && (
                    <td className="aa-chk" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={!!checkedSet?.has(r.nome)}
                        onChange={() => onToggleCheck(r.nome)} />
                    </td>
                  )}
                  <td className="aa-name" title={isLocked ? `${r.nome} · esclusa dal 456 (l'agente non la lavora)` : inPriority ? `${r.nome} · in priorità (in lavorazione)` : r.nome}>
                    {r.nome}
                    {isLocked
                      ? <span style={{ color: '#dc2626' }}><IconLock /></span>
                      : inPriority && <span className="aa-prio-dot" />}
                  </td>
                  {COLS.map(c => (
                    <td key={c.key} className={`aa-num${c.strong ? ' aa-strong' : ''}${c.highlight ? ' aa-ready' : ''}`}>
                      {c.bar ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, justifyContent: 'flex-end' }}>
                          <span className="aa-bar" style={{ width: 40 }}><span className="aa-bar-fill" style={{ width: `${pct}%`, background: barColor(pct) }} /></span>
                          <span className="aa-pct">{pct}%</span>
                        </span>
                      ) : (r[c.key] || 0).toLocaleString('it-IT')}
                    </td>
                  ))}
                  <td className="aa-bar-c">
                    <span className="aa-bar"><span className="aa-bar-fill" style={{ width: `${pct}%`, background: barColor(pct) }} /></span>
                    <span className="aa-pct">{pct}%</span>
                    {onPriority && pct < 100 && !inPriority && !isLocked && (
                      <button type="button"
                        title="Manda a priorità 456 (verifica completa di questa zona)"
                        onClick={(e) => { e.stopPropagation(); onPriority(r.nome); }}
                        style={{ marginLeft: 6, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent-dark, #b5480a)', padding: 0, lineHeight: 0, verticalAlign: 'middle' }}>
                        <IconPriorita />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {sorted.length === 0 && (
              <tr><td colSpan={COLS.length + 2 + (selectable ? 1 : 0)} className="evo-empty">Nessun dato</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
